import React, { useState } from 'react';
import { IonButton, IonIcon, IonItem, IonLabel, IonList, IonPopover } from '@ionic/react';
import { closeOutline, ellipsisVertical, printOutline, receiptOutline } from 'ionicons/icons';

interface AtendimentoHistorico {
  pk: string;
  clienteNome?: string;
  statusWorkflow?: string;
  dataCadastro: string;
  valorTotal?: string;
}

interface AcaoHistorico {
  rotulo: string;
  icone: string;
  executar: () => void;
}

interface Props {
  colunasPedido?: boolean;
  rotuloAtendimento?: string;
  renderAtendimento?: (item: AtendimentoHistorico) => React.ReactNode;
  acoesAdicionais?: (item: AtendimentoHistorico) => AcaoHistorico[];
  rotuloCupom?: string;
  filtros?: React.ReactNode;
  itens: AtendimentoHistorico[];
  carregando: boolean;
  esconderValores: boolean;
  onDetalhes: (item: AtendimentoHistorico) => void;
  onCupom: (item: AtendimentoHistorico) => void;
}

const HistoricoAtendimentosLista: React.FC<Props> = ({ filtros, itens, carregando, esconderValores, onDetalhes, onCupom, rotuloAtendimento = 'Atendimento', renderAtendimento, acoesAdicionais, rotuloCupom = 'Cupom', colunasPedido = false }) => {
  const [menu, setMenu] = useState<{ item: AtendimentoHistorico; event: Event }>();
  const grupos = itens.reduce<{ data: string; itens: AtendimentoHistorico[] }[]>((resultado, item) => {
    const data = new Date(item.dataCadastro).toLocaleDateString('pt-BR');
    const ultimoGrupo = resultado[resultado.length - 1];
    if (ultimoGrupo?.data === data) ultimoGrupo.itens.push(item);
    else resultado.push({ data, itens: [item] });
    return resultado;
  }, []);

  return (
  <>
  <section className="historico-atendimentos-lista">
    <div className="historico-lista-heading">
      <div className="historico-lista-heading-info">
        <span className="historico-lista-icon"><IonIcon icon={receiptOutline} aria-hidden="true" /></span>
        <div>
          <h2>Histórico de atendimentos</h2>
          <p>{carregando ? 'Carregando atendimentos…' : `${itens.length} ${itens.length === 1 ? 'atendimento carregado' : 'atendimentos carregados'}`}</p>
        </div>
      </div>
      {filtros}
    </div>
    {itens.length > 0 ? (
      <div className={`historico-lista-table ${esconderValores ? 'historico-lista-sem-valores' : ''} ${colunasPedido ? 'historico-lista-pedidos' : ''}`} role="table" aria-label="Histórico de atendimentos">
        <div className="historico-lista-head" role="rowgroup">
          <div className="historico-lista-row" role="row">
            <div role="columnheader">{rotuloAtendimento}</div>
            {colunasPedido && <><div role="columnheader">Cliente</div><div role="columnheader">Status</div></>}
            <div role="columnheader">Hora</div>
            {!esconderValores && <div className="historico-lista-total" role="columnheader">Total</div>}
            <div className="historico-lista-actions" role="columnheader">Ações</div>
          </div>
        </div>
        {grupos.map((grupo, grupoIndex) => (
        <div role="rowgroup" key={`${grupo.data}-${grupoIndex}`}>
          <div className="historico-lista-group-date" role="row">
            <div role="cell" aria-colspan={(esconderValores ? 3 : 4) + (colunasPedido ? 2 : 0)}><h3>{grupo.data}</h3></div>
          </div>
          {grupo.itens.map((item, index) => (
            <div className="historico-lista-row historico-lista-row-clickable" role="row" key={`${item.pk}-${index}`}
              tabIndex={carregando ? undefined : 0} aria-label={`Abrir detalhes de ${item.clienteNome || 'Balcão'}`}
              onClick={() => { if (!carregando) onDetalhes(item); }}
              onKeyDown={event => {
                if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  if (!carregando) onDetalhes(item);
                }
              }}>
              <div className="historico-lista-name" role="cell">{renderAtendimento ? renderAtendimento(item) : <strong>{item.clienteNome || 'Balcão'}</strong>}</div>
              {colunasPedido && <>
                <div className="historico-lista-cliente" role="cell">{item.clienteNome || '—'}</div>
                <div className="historico-lista-status-cell" role="cell">{item.statusWorkflow ? <span className="historico-lista-status">{item.statusWorkflow}</span> : '—'}</div>
              </>}
              <div className="historico-lista-date" role="cell">
                <span>{new Date(item.dataCadastro).toLocaleTimeString('pt-BR')}</span>
              </div>
              {!esconderValores && <div className="historico-lista-total" role="cell"><strong>R$ {item.valorTotal || '0,00'}</strong></div>}
              <div className="historico-lista-actions" role="cell" onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
                <IonButton className="historico-lista-menu" fill="clear" disabled={carregando || undefined}
                  aria-label={`Ações de ${item.clienteNome || 'Balcão'}`} aria-haspopup="dialog" aria-expanded={menu?.item === item}
                  onClick={event => setMenu({ item, event: event.nativeEvent })}>
                  <IonIcon slot="icon-only" icon={ellipsisVertical} aria-hidden="true" />
                </IonButton>
              </div>
            </div>
          ))}
        </div>
        ))}
      </div>
    ) : !carregando && (
      <div className="historico-atendimentos-empty">
        <h3>Nenhum atendimento encontrado</h3>
        <p>Selecione outra data para consultar o histórico.</p>
      </div>
    )}
  </section>
  <IonPopover className="historico-lista-popover" isOpen={!!menu} event={menu?.event}
    onDidDismiss={() => setMenu(undefined)}>
    <IonList>
      {menu && acoesAdicionais?.(menu.item).map(acao => (
        <IonItem key={acao.rotulo} button lines="none" disabled={carregando || undefined} onClick={() => {
          setMenu(undefined);
          if (!carregando) acao.executar();
        }}>
          <IonIcon slot="start" icon={acao.icone} aria-hidden="true" />
          <IonLabel>{acao.rotulo}</IonLabel>
        </IonItem>
      ))}
      <IonItem button lines="none" disabled={carregando || undefined} onClick={() => {
        const item = menu?.item;
        setMenu(undefined);
        if (item && !carregando) onCupom(item);
      }}>
        <IonIcon slot="start" icon={printOutline} aria-hidden="true" />
        <IonLabel>{rotuloCupom}</IonLabel>
      </IonItem>
      <IonItem button lines="none" onClick={() => setMenu(undefined)}>
        <IonIcon slot="start" icon={closeOutline} aria-hidden="true" />
        <IonLabel>Cancelar</IonLabel>
      </IonItem>
    </IonList>
  </IonPopover>
  </>
  );
};

export default HistoricoAtendimentosLista;
